import * as React from "react";
import { useState } from "react";

import type { Schemas } from "@fluid-app/fluid";
import {
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@fluid-app/ui";

// You may want to import a spinner or loading indicator from your UI lib

interface CartSubscriptionsProps {
  item: Schemas["CartItem"];
  onPlanChange?: (
    item: Schemas["CartItem"],
    checked: boolean,
    planId: number,
  ) => Promise<void>;
}

function getSubscriptionTitle(
  plan: Partial<NonNullable<Schemas["CartItem"]["subscription_plans"]>[number]>,
) {
  if (!plan) return "";
  const interval = plan.billing_interval;
  const unit = plan.billing_interval_unit;
  if (!interval || !unit) return "";
  const base =
    unit === "month" && interval === 1
      ? `Every month`
      : `Every ${interval} ${unit}${interval !== 1 ? "s" : ""}`;
  return plan.subscribe_and_save_for_display
    ? `${base}, ${plan.subscribe_and_save_for_display}`
    : base;
}

export const CartSubscriptions: React.FC<CartSubscriptionsProps> = ({
  item,
  onPlanChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(!!item.subscription);

  // Add state to track selected subscription plan per item
  const [selectedSubscriptionPlanId, setSelectedSubscriptionPlanId] = useState<
    number | null
  >(item.subscription_plan_id ?? item.subscription_plans?.[0]?.id ?? null);

  const plans = item.subscription_plans?.filter(Boolean) ?? [];
  const hasPlans = plans.length > 0;
  const currentPlan = plans.find((p) => p?.id === selectedSubscriptionPlanId);

  const handleCheckbox = async (checked: boolean, selectedPlanId?: number) => {
    setLoading(true);
    try {
      setSubscribed(checked);
      const planId =
        selectedPlanId ??
        selectedSubscriptionPlanId ??
        item.subscription_plan_id ??
        item.subscription_plans?.[0]?.id;
      if (planId) {
        await onPlanChange?.(item, checked, planId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (planId: number) => {
    if (planId === selectedSubscriptionPlanId) return;
    setLoading(true);
    try {
      setSelectedSubscriptionPlanId(planId);
      await onPlanChange?.(item, true, planId);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    // handle edge case for subscription only items that were added incorrectly
    if (item.subscription_only && !subscribed) {
      const doSubscribe = async () => {
        setSubscribed(true);
        const defaultSubscribedPlanId =
          item.subscription_plan_id ?? item.subscription_plans?.[0]?.id;
        if (defaultSubscribedPlanId) {
          setSelectedSubscriptionPlanId(defaultSubscribedPlanId);
        }
        await handleCheckbox(true, defaultSubscribedPlanId);
      };
      void doSubscribe();
    }
  }, []);

  if (!item.allow_subscription || !hasPlans) return null;

  return (
    <div className="flex flex-col gap-1">
      <div className="mt-1 flex items-center">
        <Checkbox
          id={`subscribe-${item.id}`}
          checked={subscribed}
          onCheckedChange={async (checked: boolean) =>
            handleCheckbox(Boolean(checked))
          }
          className="mr-1.5"
          disabled={loading || item.subscription_only}
        />
        <label
          htmlFor={`subscribe-${item.id}`}
          className="cursor-pointer text-xs text-muted-foreground"
        >
          {item.subscribe_and_save_for_display ??
            `Save ${item.subscription_price_in_currency}`}
        </label>
      </div>
      {subscribed && plans.length > 1 && (
        <div className="ml-2 mt-1 text-xs">
          <Select
            value={selectedSubscriptionPlanId?.toString() ?? ""}
            onValueChange={(val) => {
              const planId = Number(val);
              if (!loading && planId) void handleSelect(planId);
            }}
            open={open}
            onOpenChange={setOpen}
            disabled={loading}
          >
            <SelectTrigger className="max-w-[200px] overflow-hidden rounded border bg-background px-3 py-2 text-left text-sm disabled:opacity-50">
              {currentPlan
                ? getSubscriptionTitle(currentPlan)
                : "Select plan..."}
            </SelectTrigger>
            <SelectContent
              className="z-[999999] min-h-[100px] min-w-[200px] max-w-[200px] rounded-md border border-gray-200 bg-white"
              style={{
                left: 0,
                top: 5,
                minHeight: 50,
                width: 250,
                position: "fixed",
                zIndex: 999999,
                backgroundColor: "white",
                padding: 4,
                boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                borderRadius: 8, // rounded-md
                fontSize: "0.75rem", // text-xs
              }}
            >
              <div className="flex flex-col gap-1 rounded-md border border-gray-200 bg-white p-2">
                {plans.map((plan, idx) => (
                  <SelectItem
                    key={plan?.id}
                    value={plan?.id?.toString() ?? ""}
                    style={{
                      padding: 4,
                      ...(idx !== plans.length - 1
                        ? { borderBottom: "1px solid #f5f5f5" }
                        : {}),
                      marginTop: 2,
                      minHeight: 25,
                    }}
                  >
                    <span>{getSubscriptionTitle(plan)}</span>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default CartSubscriptions;
